import { inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { pipe, switchMap, tap } from 'rxjs';
import {
  setPropError,
  setPropInit,
  setPropLoaded,
  setPropLoading,
  withReqState,
} from '@application/data-access';
import { ErrorStore } from '@application/errors/data-access';
import { AbsenceLimitSettingsService } from '../infra/absence-limit-settings.service';
import {
  AbsenceLimitSettings,
  AbsenceLimitSettingsRequest,
  AbsenceLimitSettingsState,
  initialAbsenceLimitSettingsState,
} from '../entities/absence-limit-settings.model';

const OPS = ['load', 'update', 'updateNotices'] as const;

export const AbsenceLimitSettingsStore = signalStore(
  { providedIn: 'root' },
  withState<AbsenceLimitSettingsState>(initialAbsenceLimitSettingsState),
  withReqState(...OPS),
  withMethods(
    (
      store,
      service = inject(AbsenceLimitSettingsService),
      errorStore = inject(ErrorStore)
    ) => ({
      load: rxMethod<void>(
        pipe(
          tap(() => patchState(store, setPropLoading('load'))),
          switchMap(() =>
            service.get().pipe(
              tapResponse({
                next: (absenceLimitSettings: AbsenceLimitSettings) =>
                  patchState(store, { absenceLimitSettings }, setPropLoaded('load')),
                error: (e: HttpErrorResponse) => patchState(store, setPropError('load', e)),
              })
            )
          )
        )
      ),

      update: rxMethod<AbsenceLimitSettingsRequest>(
        pipe(
          tap(() => patchState(store, setPropLoading('update'))),
          switchMap((body) =>
            service.update(body).pipe(
              tapResponse({
                next: (absenceLimitSettings: AbsenceLimitSettings) =>
                  patchState(store, { absenceLimitSettings }, setPropLoaded('update')),
                error: (e: HttpErrorResponse) => {
                  patchState(store, setPropError('update', e));
                  errorStore.setHttpError(e);
                },
              })
            )
          )
        )
      ),

      updateNotices: rxMethod<AbsenceLimitSettingsRequest>(
        pipe(
          tap(() => patchState(store, setPropLoading('updateNotices'))),
          switchMap((body) =>
            service.update(body).pipe(
              tapResponse({
                next: (absenceLimitSettings: AbsenceLimitSettings) =>
                  patchState(
                    store,
                    { absenceLimitSettings },
                    setPropLoaded('updateNotices')
                  ),
                error: (e: HttpErrorResponse) => {
                  patchState(store, setPropError('updateNotices', e));
                  errorStore.setHttpError(e);
                },
              })
            )
          )
        )
      ),

      blankUpdateNoticesState(): void {
        patchState(store, setPropInit('updateNotices'));
      },

      blankState(): void {
        patchState(store, initialAbsenceLimitSettingsState, setPropInit(...OPS));
      },
    })
  )
);
